/**
 * Firebase Error Handler
 * Converts Firebase error codes to user-friendly messages
 * Handles network errors, auth errors, and other Firebase exceptions
 */

// Network-related error codes
const NETWORK_ERROR_CODES = [
  'auth/network-request-failed',
  'auth/internal-error',
  'auth/operation-not-supported-in-this-environment',
]

// Authentication-specific error codes
const AUTH_ERROR_CODES: Record<string, string> = {
  'auth/email-already-in-use': 'emailAlreadyInUse',
  'auth/invalid-email': 'invalidEmail',
  'auth/user-not-found': 'userNotFound',
  'auth/wrong-password': 'wrongPassword',
  'auth/weak-password': 'weakPassword',
  'auth/too-many-requests': 'tooManyRequests',
  'auth/user-disabled': 'userDisabled',
  'auth/requires-recent-login': 'requiresRecentLogin',
}

interface FirebaseError {
  code?: string
  message?: string
}

/**
 * Converts a Firebase error to a translation key for user-friendly messages
 * @param error - The Firebase error object
 * @returns Translation key for the error message
 */
export function getFirebaseErrorKey(error: unknown): string {
  const firebaseError = error as FirebaseError

  // Check if it's a network error
  if (firebaseError?.code && NETWORK_ERROR_CODES.includes(firebaseError.code)) {
    return 'networkError'
  }

  // Check for specific auth errors
  if (firebaseError?.code && AUTH_ERROR_CODES[firebaseError.code]) {
    return AUTH_ERROR_CODES[firebaseError.code]
  }

  // Default error
  return 'loginFailed'
}

/**
 * Checks if an error is a network error
 * @param error - The Firebase error object
 * @returns True if the error is network-related
 */
export function isNetworkError(error: unknown): boolean {
  const firebaseError = error as FirebaseError
  return (
    firebaseError?.code !== undefined &&
    NETWORK_ERROR_CODES.includes(firebaseError.code)
  )
}

/**
 * Checks if an error is an "too many requests" error (rate limiting)
 * @param error - The Firebase error object
 * @returns True if the error is a rate limit error
 */
export function isTooManyRequestsError(error: unknown): boolean {
  const firebaseError = error as FirebaseError
  return firebaseError?.code === 'auth/too-many-requests'
}

/**
 * Gets the Firebase error code from an error object
 * @param error - The error object
 * @returns The error code or undefined
 */
export function getErrorCode(error: unknown): string | undefined {
  return (error as FirebaseError)?.code
}
